// Amethyst geodes (M19.1). PURE (ENG-1): the budding-growth rules a geode's
// `budding_amethyst` blocks follow when the host random-ticks them, plus the
// shard yield of a broken cluster. A budding block probes one random face per
// tick and, at Minecraft's 1-in-5 odds, sprouts a small bud on an open face or
// advances one of its own buds a stage: small → medium → large → cluster.
import { AMETHYST } from '../config';

/** The four growth stages, ascending. Each is a `facing6`-attached block whose
 *  facing points away from the budding block that grew it. */
export const AMETHYST_LADDER = [
  'small_amethyst_bud', 'medium_amethyst_bud', 'large_amethyst_bud', 'amethyst_cluster',
] as const;
export type AmethystBud = (typeof AMETHYST_LADDER)[number];

/** A face a budding block can grow into (in the canonical `facing6` order). */
export interface GrowDir {
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
  readonly facing: string;
}
export const GROW_DIRS: readonly GrowDir[] = [
  { dx: 0, dy: 0, dz: -1, facing: 'north' },
  { dx: 1, dy: 0, dz: 0, facing: 'east' },
  { dx: 0, dy: 0, dz: 1, facing: 'south' },
  { dx: -1, dy: 0, dz: 0, facing: 'west' },
  { dx: 0, dy: 1, dz: 0, facing: 'up' },
  { dx: 0, dy: -1, dz: 0, facing: 'down' },
];

/** A budding block sprouts only into an open face — air or water (a geode void). */
export function isGrowMedium(block: string | null): boolean {
  return block === null || block === 'air' || block === 'water';
}

export interface BudGrowth {
  readonly dir: GrowDir;
  readonly block: AmethystBud;
}

/**
 * Resolve one budding-growth attempt on the neighbour at `dirIndex`. `roll01`
 * gates it (Minecraft: 1-in-5 per random tick). An open face → a small bud; one
 * of our own buds facing back at us → the next stage; anything else (stone, a
 * full cluster, or a bud grown by a *different* budding block) → no growth.
 * Returns the block to place and the facing it carries, or null.
 */
export function rollBudGrowth(
  roll01: number,
  dirIndex: number,
  neighbourBlock: string | null,
  neighbourFacing: string | null,
): BudGrowth | null {
  if (roll01 >= AMETHYST.GROW_CHANCE) return null;
  const dir = GROW_DIRS[dirIndex];
  if (!dir) return null;
  if (isGrowMedium(neighbourBlock)) return { dir, block: 'small_amethyst_bud' };
  const i = (AMETHYST_LADDER as readonly string[]).indexOf(neighbourBlock ?? '');
  if (i < 0 || i + 1 >= AMETHYST_LADDER.length) return null; // not ours, or already a full cluster
  if (neighbourFacing !== dir.facing) return null; // a bud grown by a different budding block
  return { dir, block: AMETHYST_LADDER[i + 1]! };
}

/** Amethyst shards a broken bud/cluster yields — only the full cluster drops
 *  (buds need Silk Touch, deferred). The block loot table mirrors this count. */
export function clusterShards(block: string): number {
  return block === 'amethyst_cluster' ? AMETHYST.CLUSTER_SHARDS : 0;
}
